/**
 * The sale that has just gone through, and the paper that goes with it.
 *
 * Shown the moment checkout returns, because the two things a cashier needs next are on
 * it: the change to hand over, and a receipt. Both come from the server's answer -- the
 * change is the figure the drawer has to reconcile against, and the receipt is rendered
 * from the sale row rather than from anything this screen holds.
 *
 * It is also the reprint screen: the same route reached from the shift's sales list shows
 * a sale from an hour ago, with the same buttons.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, Check, Download, Mail, Printer, ShoppingCart } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { useMayOpen } from '@/features/auth/access'
import { ApiError } from '@/api/client'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { QueryState } from '@/components/QueryState'
import { useAuth } from '@/features/auth/store'
import { useRegime } from '@/features/company/regime'
import {
  TENDER_LABELS,
  emailReceipt,
  fetchReceiptHtml,
  fetchReceiptPdf,
  formatMoney,
  getSale,
  voidSale,
  type Sale,
} from '@/features/pos/api'
import { formatPercent } from '@/lib/money'
import { toast } from '@/lib/toast'

export function PosReceipt() {
  const { transactionId = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const canVoid = useAuth((state) => state.can('pos', 'delete'))
  const canEmail = useAuth((state) => state.can('pos', 'create'))
  // A receipt is readable by roles that may not work a till -- a finance manager checking
  // a day's takings holds `pos:read` and nothing more -- so the two ways back to the
  // counter come off for them rather than bouncing off the guard on `/pos`.
  const canSell = useMayOpen('/pos')

  const [dialog, setDialog] = useState<'none' | 'email' | 'void'>('none')

  const sale = useQuery({
    queryKey: ['pos', 'sale', transactionId],
    queryFn: () => getSale(transactionId),
  })

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <QueryState isLoading={sale.isLoading} error={sale.error}>
        {sale.data && (
          <SaleView
            sale={sale.data}
            canVoid={canVoid}
            canEmail={canEmail}
            canSell={canSell}
            onEmail={() => setDialog('email')}
            onVoid={() => setDialog('void')}
            onNewSale={() => navigate('/pos')}
          />
        )}
      </QueryState>

      {sale.data && (
        <>
          <EmailDialog
            open={dialog === 'email'}
            onClose={() => setDialog('none')}
            saleId={sale.data.id}
          />
          <VoidDialog
            open={dialog === 'void'}
            onClose={() => setDialog('none')}
            saleId={sale.data.id}
            onVoided={() => {
              setDialog('none')
              void queryClient.invalidateQueries({ queryKey: ['pos'] })
            }}
          />
        </>
      )}
    </div>
  )
}

function SaleView({
  sale,
  canVoid,
  canEmail,
  canSell,
  onEmail,
  onVoid,
  onNewSale,
}: {
  sale: Sale
  canVoid: boolean
  canSell: boolean
  canEmail: boolean
  onEmail: () => void
  onVoid: () => void
  onNewSale: () => void
}) {
  // The regime the *sale* was rung up under, not the one in force today. A receipt
  // reprinted after a jurisdiction change still has to name the taxes it charged.
  const regime = useRegime(sale.tax_regime)
  const components = regime.component_labels[sale.supply_type] ?? [null, null, null]
  const printFrame = useRef<HTMLIFrameElement | null>(null)

  // The frame outlives the print dialog -- the browser needs it while the dialog is open --
  // so it is taken away when the screen goes, not when printing returns.
  useEffect(() => () => printFrame.current?.remove(), [])

  const print = useMutation({
    mutationFn: () => fetchReceiptHtml(sale.id),
    onSuccess: (html) => {
      // Printed from a hidden frame rather than a new tab: a popup blocker between the
      // cashier and the customer's receipt is not a failure mode worth having, and the
      // server's stylesheet already sizes the roll.
      const frame = document.createElement('iframe')
      frame.setAttribute('aria-hidden', 'true')
      frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
      frame.srcdoc = html
      frame.onload = () => {
        frame.contentWindow?.focus()
        frame.contentWindow?.print()
      }
      printFrame.current?.remove()
      printFrame.current = frame
      document.body.appendChild(frame)
    },
    onError: () => toast.error('Could not fetch the receipt to print.'),
  })

  const download = useMutation({
    mutationFn: () => fetchReceiptPdf(sale.id),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `${sale.receipt_number}.pdf`
      anchor.click()
      // Not revoked on the next line: Firefox cancels a download whose blob URL is
      // released before it has started reading it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    },
    onError: () => toast.error('Could not fetch the receipt PDF.'),
  })

  const voided = sale.status === 'voided'

  return (
    <>
      <Card>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400">
                {voided ? (
                  <Ban className="h-4 w-4 text-danger" aria-hidden="true" />
                ) : (
                  <Check className="h-4 w-4 text-success" aria-hidden="true" />
                )}
                {voided ? 'Voided sale' : 'Sale complete'}
              </p>
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
                {sale.receipt_number}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {new Date(sale.completed_at).toLocaleString()}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Total
              </p>
              <p className="text-3xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                {formatMoney(sale.total)}
              </p>
            </div>
          </div>

          {voided && (
            <Alert tone="error" title="This sale was voided">
              {sale.void_reason} The stock went back and the money came out; the receipt reprints
              marked VOIDED.
            </Alert>
          )}

          {Number(sale.change_given) > 0 && !voided && (
            <div className="rounded-md bg-brand-50 p-4 text-center dark:bg-brand-900/30">
              <p className="text-xs uppercase tracking-wide text-brand-700 dark:text-brand-300">
                Change due
              </p>
              <p className="text-4xl font-semibold tabular-nums text-brand-700 dark:text-brand-200">
                {formatMoney(sale.change_given)}
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {/* Focused on arrival: the cashier's next act, nine times in ten, is the next
                customer, and it should not need the mouse. */}
            {canSell && (
              <Button autoFocus onClick={onNewSale}>
                <ShoppingCart className="h-4 w-4" />
                New sale
              </Button>
            )}
            <Button variant="secondary" loading={print.isPending} onClick={() => print.mutate()}>
              <Printer className="h-4 w-4" />
              Print
            </Button>
            <Button
              variant="secondary"
              loading={download.isPending}
              onClick={() => download.mutate()}
            >
              <Download className="h-4 w-4" />
              PDF
            </Button>
            {canEmail && (
              <Button variant="secondary" onClick={onEmail}>
                <Mail className="h-4 w-4" />
                Email
              </Button>
            )}
            {canVoid && !voided && (
              <Button variant="ghost" className="text-danger" onClick={onVoid}>
                <Ban className="h-4 w-4" />
                Void
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="What was sold" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
              <tr>
                <th scope="col" className="px-5 py-2">
                  Item
                </th>
                <th scope="col" className="px-5 py-2 text-center">
                  Qty
                </th>
                <th scope="col" className="px-5 py-2 text-right">
                  Price
                </th>
                <th scope="col" className="px-5 py-2 text-right">
                  Tax
                </th>
                <th scope="col" className="px-5 py-2 text-right">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {sale.items.map((item) => (
                <tr key={item.id}>
                  <td className="px-5 py-2">
                    <span className="block text-slate-900 dark:text-slate-100">
                      {item.product_data.name ?? '—'}
                    </span>
                    <span className="block text-xs text-slate-500 dark:text-slate-400">
                      {item.product_data.code}
                      {item.tax_code && ` · ${regime.product_code_label} ${item.tax_code}`}
                    </span>
                  </td>
                  <td className="px-5 py-2 text-center tabular-nums">{item.quantity}</td>
                  <td className="px-5 py-2 text-right tabular-nums">
                    {formatMoney(item.unit_price)}
                  </td>
                  <td className="px-5 py-2 text-right tabular-nums">
                    {formatMoney(item.tax_amount)}
                    <span className="block text-xs text-slate-500 dark:text-slate-400">
                      {formatPercent(item.tax_rate)}
                    </span>
                  </td>
                  <td className="px-5 py-2 text-right font-medium tabular-nums">
                    {formatMoney(item.line_total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <CardBody className="border-t border-slate-200 dark:border-slate-800">
          <dl className="ml-auto max-w-sm space-y-1.5 text-sm">
            {/* `subtotal` is stored net of the discount; shown above the discount line it
                would take the discount off twice. */}
            <Line
              label="Subtotal"
              value={formatMoney((Number(sale.subtotal) + Number(sale.discount_amount)).toFixed(2))}
            />
            {Number(sale.discount_amount) > 0 && (
              <Line label="Discount" value={`− ${formatMoney(sale.discount_amount)}`} />
            )}
            {/* Named by the regime, never by the field: two authorities on one supply fill
                primary and secondary, one fills unified. */}
            {components[0] && Number(sale.primary_tax_amount) > 0 && (
              <Line label={components[0]} value={formatMoney(sale.primary_tax_amount)} />
            )}
            {components[1] && Number(sale.secondary_tax_amount) > 0 && (
              <Line label={components[1]} value={formatMoney(sale.secondary_tax_amount)} />
            )}
            {components[2] && Number(sale.unified_tax_amount) > 0 && (
              <Line label={components[2]} value={formatMoney(sale.unified_tax_amount)} />
            )}
            {Number(sale.round_off) !== 0 && (
              <Line label="Rounding" value={formatMoney(sale.round_off)} />
            )}
            <Line label="Total" value={formatMoney(sale.total)} strong />
            {sale.payments.map((payment) => (
              <Line
                key={payment.id}
                label={TENDER_LABELS[payment.method] ?? payment.method}
                value={formatMoney(payment.amount)}
                muted
              />
            ))}
            {Number(sale.cash_tendered) > 0 && (
              <>
                <Line label="Cash given" value={formatMoney(sale.cash_tendered)} muted />
                <Line label="Change" value={formatMoney(sale.change_given)} muted />
              </>
            )}
          </dl>
        </CardBody>
      </Card>

      {canSell && (
        <p className="text-center text-sm">
          <Link to="/pos/shift" className="text-brand-600 hover:underline dark:text-brand-400">
            Shift and takings
          </Link>
        </p>
      )}
    </>
  )
}

function Line({
  label,
  value,
  strong,
  muted,
}: {
  label: string
  value: string
  strong?: boolean
  muted?: boolean
}) {
  return (
    <div
      className={
        strong
          ? 'flex justify-between border-t border-slate-200 pt-1.5 text-base font-semibold text-slate-900 dark:border-slate-800 dark:text-slate-100'
          : 'flex justify-between'
      }
    >
      <dt
        className={
          muted ? 'text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-400'
        }
      >
        {label}
      </dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  )
}

function EmailDialog({
  open,
  onClose,
  saleId,
}: {
  open: boolean
  onClose: () => void
  saleId: string
}) {
  const [address, setAddress] = useState('')

  const send = useMutation({
    mutationFn: () => emailReceipt(saleId, address.trim() || undefined),
    onSuccess: (sent) => {
      toast.success(`Receipt sent to ${sent.email}.`)
      setAddress('')
      onClose()
    },
    onError: (error) => {
      toast.error(
        error instanceof ApiError && error.status === 422
          ? 'That address will not do. Type one, or attach a customer to the sale.'
          : 'Could not send the receipt.',
      )
    },
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Email the receipt"
      description="Leave it blank to use the address on the customer's account."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={send.isPending} onClick={() => send.mutate()}>
            Send
          </Button>
        </>
      }
    >
      <Input
        type="email"
        aria-label="Email address"
        placeholder="name@example.com"
        value={address}
        onChange={(event) => setAddress(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') send.mutate()
        }}
      />
    </Modal>
  )
}

function VoidDialog({
  open,
  onClose,
  saleId,
  onVoided,
}: {
  open: boolean
  onClose: () => void
  saleId: string
  onVoided: () => void
}) {
  const [reason, setReason] = useState('')

  const cancel = useMutation({
    mutationFn: () => voidSale(saleId, reason.trim()),
    onSuccess: () => {
      toast.success('Sale voided. The stock has gone back.')
      setReason('')
      onVoided()
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not void the sale.'),
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Void this sale"
      description="The sale stays on the record, marked voided, with your name against it."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Keep the sale
          </Button>
          <Button
            variant="danger"
            loading={cancel.isPending}
            disabled={reason.trim().length < 3}
            onClick={() => cancel.mutate()}
          >
            Void the sale
          </Button>
        </>
      }
    >
      <Input
        aria-label="Why is this being voided?"
        placeholder="Wrong item scanned, customer changed their mind…"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
    </Modal>
  )
}
